import {style} from "../../app/utils/load";
import {useDispatch, useSelector} from "react-redux";
import {
    Clusterer,
    Map,
    Placemark,
    Polygon,
    Polyline,
    YMaps
} from "react-yandex-maps";
import {useCity} from "../../app/utils/use";
import {findCityCenter, findObjectsByLatLng} from "../../app/utils/latLng";
import {CITY_LIST, ONSITE_DRYCLEANING_SERVICE} from "../../app/utils/const";
import {createRef, useEffect, useRef, useState} from "react";
import FilterPanel from "./filter/FilterPanel";
import {
    clearDrives,
    clearZones,
    loadDrivesAsync,
    loadZonesAsync,
    selectDrives,
    selectZones,
    updateDrive,
    loadGeoObjectsInInfo,
    selectGeoObjectsInInfoWindow
} from "./object/onsiteDrycleaningMapObjectSlice";
import {
    deactivateActiveItem,
    loadEmployeesAsync,
    selectActiveItem,
    selectEmployees,
    setSearchText
} from "../employee/employeeSlice";
import CentralPanel from "./panel/CentralPanel";
import _ from "lodash";
import {
    filterEmployeesByName,
    loadAllEmployeesAsync,
    selectAllEmployees,
    selectFilteredEmployees,
    selectFilterIsApply
} from "./employee/employeeSlice";
import EmployeeBody from "./employee/EmployeeBody";
import EmployeeList from "../employee/EmployeeList";
import {loadRoutesAsync, selectDrivesInRoutes, addToRoute} from "./route/routeSlice";
import WsClient from "./ws/WsClient";
import {selectAuthKey} from "../ws/wsSlice";
import moment from "moment";
import routeStyles from '../cleaning/route/Route.module.css';
import {renderToStaticMarkup} from "react-dom/server";
import DriveBalloon from "./balloon/DriveBalloon";
import Info from "../window/Info";
import OnsiteContent from "./window/content/OnsiteContent";
import axios from "axios";

console.log('OnsiteDrycleaningLogisticApp ver: 1.0.0');

const API_BASE = '/onsite-drycleaning/clsystem/api/v1';
const EMPLOYEE_API_BASE = API_BASE + '/onsite-drycleaner';

function OnsiteDrycleaningApp() {
    style('//maxcdn.bootstrapcdn.com/bootswatch/3.3.7/paper/bootstrap.min.css', 'boostrap-css');

    const dispatch = useDispatch();
    const [zonePolygons, setZonePolygons] = useState([]);
    const [drivePlacemarks, setDrivePlacemarks] = useState([]);
    const [showEmployeesByType, setShowEmployeesByType] = useState(null);
    const [filterDrivesByIds, setFilterDrivesByIds] = useState([]);
    const [polyline, setPolyline] = useState(null);
    const [zoneIdsPickedInFilter, setZoneIdsPickedInFilter] = useState([]);

    const drivesInRoutes = useSelector(selectDrivesInRoutes);
    const authKey = useSelector(selectAuthKey);
    const employeeFilterIsApply = useSelector(selectFilterIsApply);
    const activeEmployee = useSelector(selectActiveItem);
    const geoObjectsInInfoWindow = useSelector(selectGeoObjectsInInfoWindow);

    const baseEmployeesList = useSelector(selectEmployees);
    const filteredEmployeesList = useSelector(selectFilteredEmployees);
    const allEmployees = useSelector(selectAllEmployees);

    const employeeList = employeeFilterIsApply ? filteredEmployeesList : baseEmployeesList;
    const employeeListForSidebar = showEmployeesByType === 1
        ? allEmployees
        : employeeList;
    const employees = _.sortBy(employeeListForSidebar, (employee) => [employee.name]);

    const mapRef = useRef(null);
    const clustererRef = createRef(null);
    const cityId = useCity();
    const cityCenter = findCityCenter(CITY_LIST, cityId);

    const zones = useSelector(selectZones);
    const drives = useSelector(selectDrives);

    useEffect(() => {
        setDrivePlacemarks([]);
        if (!drives.length) return;

        let visibleDrives = drives.filter((drive) => {
            if (!drive.addressData?.lat || !drive.addressData?.lng) return false;
            if (zoneIdsPickedInFilter.length > 0) {
                const driveZoneId = drive.addressData?.zone?.zoneId;
                if (!driveZoneId || !_.includes(zoneIdsPickedInFilter, driveZoneId)) {
                    return false;
                }
            }
            if (filterDrivesByIds.length && !_.includes(filterDrivesByIds, drive.id)) {
                return false;
            }
            return true;
        });

        const placemarks = visibleDrives.map((drive) => (
            <Placemark
                options={{
                    iconLayout: 'default#image',
                    iconImageHref: '/icon/onsite-drycleaning/' + drive.icon,
                    iconImageSize: [30, 42],
                    iconImageOffset: [-15, -42],
                    zIndex: 2
                }}
                geometry={[drive.addressData.lat, drive.addressData.lng]}
                properties={{
                    icon: drive.icon,
                    statusLabel: drive.statusLabel,
                    driveId: drive.id
                }}
                key={'drive-' + drive.id}
            />
        ));
        setDrivePlacemarks(placemarks);
    }, [drives, zoneIdsPickedInFilter, filterDrivesByIds]);

    let employeesAddressesPlacemarks = [];
    if (showEmployeesByType) {
        const employeesByType = showEmployeesByType === 1 ? allEmployees : baseEmployeesList;
        employeesAddressesPlacemarks = _.map(
            employeesByType.filter((employee) => employee.addressBook !== null),
            (employee) => (
                <Placemark
                    options={{
                        zIndex: 2,
                        preset: "islands#blueStretchyIcon",
                    }}
                    properties={{
                        iconContent: employee.name,
                    }}
                    geometry={[employee.addressBook.lat, employee.addressBook.lng]}
                    key={'cleaner-' + employee.id}
                />
            )
        );
    }

    useEffect(() => {
        setZonePolygons([]);
        if (zones.length) {
            const filteredZones = zones.filter((zone) => zone.isVisible);
            const polygons = [];
            _.each(filteredZones, (zone, key) => {
                polygons.push(
                    <Polygon
                        options={{
                            fillColor: zone.color, opacity: 0.5, strokeColor: '#000000', strokeWidth: 2,
                        }}
                        geometry={[zone.geometry.points.map((latLng) => [latLng.lat, latLng.lng]), []]}
                        key={'zone' + key}
                    />
                );
            });
            setZonePolygons(polygons);
        }
    }, [zones]);

    useEffect(() => {
        dispatch(clearZones());
        dispatch(deactivateActiveItem());
        dispatch(loadZonesAsync({cityId: cityId, serviceId: ONSITE_DRYCLEANING_SERVICE}));
        dispatch(setSearchText(''));
        dispatch(loadAllEmployeesAsync({
            url: EMPLOYEE_API_BASE + '/all',
            params: {cityId: cityId, serviceId: ONSITE_DRYCLEANING_SERVICE}
        }));
    }, [cityId]);

    const loadEmployees = (filtersValue) => {
        dispatch(loadEmployeesAsync({
            url: EMPLOYEE_API_BASE,
            params: {
                cityId: cityId,
                date: filtersValue.date,
                serviceId: ONSITE_DRYCLEANING_SERVICE,
                expand: 'schedule'
            }
        }));
        dispatch(loadRoutesAsync({
            cityId: cityId, expand: 'drive', date: filtersValue.date
        }));
    };

    const loadDrives = (filtersValue) => {
        dispatch(clearDrives());
        setZoneIdsPickedInFilter(filtersValue.zones);
        const {date, orderNumber, status, zones} = filtersValue;
        dispatch(loadDrivesAsync({
            cityId: cityId,
            expand: 'order,client',
            filter: JSON.stringify({date, orderNumber, status, zones})
        }));
    };

    const handleClickAddToRoute = (drive) => {
        const employee = _.find(baseEmployeesList, {id: activeEmployee});
        if (typeof employee === "undefined") {
            return;
        }

        if (drive.order?.categoryIds?.length) {
            const hasMatchingCategory = (employee.categoryIds || []).some(
                (catId) => drive.order.categoryIds.includes(catId)
            );
            if (!hasMatchingCategory) {
                alert(
                    'Категории заказа и мастера не совпадают.\n' +
                    'Категории заказа: [' + drive.order.categoryIds.join(', ') + ']\n' +
                    'Категории мастера: [' + (employee.categoryIds || []).join(', ') + ']'
                );
                return;
            }
        }

        if (drive?.addressData?.zone?.zoneId && employee.schedule) {
            let addressZoneExistsInCleanerSchedule = false;
            employee.schedule.forEach((scheduleItem) => {
                if ((scheduleItem.zones || []).filter((zoneInSchedule) =>
                    (drive?.addressData?.zone?.zonesTree || []).includes(zoneInSchedule.zoneId)
                ).length > 0) {
                    addressZoneExistsInCleanerSchedule = true;
                }
            });
            if (!addressZoneExistsInCleanerSchedule) {
                alert('Мастер не работает в данной зоне (' + drive?.addressData?.zone?.name + '). Необходимо добавить в расписание мастера зону: ' + drive?.addressData?.zone?.name);
            }
        }

        let scheduleItems = [];
        if (employee.schedule && drive.timeFrom && drive.timeTo) {
            const driveStartTime = moment(drive.timeFrom, 'YYYY-MM-DD HH:mm:ss');
            const driveEndTime = moment(drive.timeTo, 'YYYY-MM-DD HH:mm:ss');
            scheduleItems = _.filter(employee.schedule, (scheduleItem) => {
                const scheduleInterval = scheduleItem.interval.split(' - ');
                const scheduleStartTime = moment(drive.date + ' ' + scheduleInterval[0], 'YYYY-MM-DD HH:mm');
                const scheduleEndTime = moment(drive.date + ' ' + scheduleInterval[1], 'YYYY-MM-DD HH:mm');
                return (
                    (driveStartTime.isBetween(scheduleStartTime, scheduleEndTime) || driveStartTime.isSame(scheduleStartTime)) &&
                    (driveEndTime.isBetween(scheduleStartTime, scheduleEndTime) || driveEndTime.isSame(scheduleEndTime))
                );
            });

            if (scheduleItems.length === 0) {
                alert('Время данного заказа не входит в рабочий интервал мастера');
                return;
            }
        }

        dispatch(addToRoute({
            courier: activeEmployee,
            drive: drive,
            scheduleId: _.head(scheduleItems)?.id || drive.scheduleId || null
        }));
    };

    const closeBalloon = (e) => {
        e.getSourceEvent().originalEvent.map.balloon.close();
    };

    const removePolyline = () => {
        setPolyline(null);
    };

    const renderPolyLine = (geoObjects) => {
        const geometry = geoObjects.map((geoObject) => [geoObject.addressData.lat, geoObject.addressData.lng]);
        setPolyline(
            <Polyline geometry={geometry} options={{
                balloonCloseButton: false,
                strokeColor: '#ff4545',
                strokeWidth: 3,
                strokeOpacity: 1
            }}/>
        );
    };

    const openInfoPanel = (e) => {
        const latLng = e.getSourceEvent().originalEvent.target.geometry.getCoordinates();
        const objects = findObjectsByLatLng(latLng, drives);
        dispatch(loadGeoObjectsInInfo(objects));
        renderPolyLine(objects);
    };

    const openShortBalloon = (e) => {
        const latLng = e.getSourceEvent().originalEvent.target.geometry.getCoordinates();
        const map = e.getSourceEvent().originalEvent.map;
        const objects = findObjectsByLatLng(latLng, drives);

        const components = objects.map((object, key) =>
            renderToStaticMarkup(<DriveBalloon key={'drive-balloon-' + key} drive={object}/>)
        );

        const offset = objects.length > 1 ? [0, -10] : [-1, -32];

        if (objects.length > 0) {
            map.balloon.open(
                [objects[0].addressData.lat, objects[0].addressData.lng],
                components.join('<hr/>'),
                {offset: offset}
            );
        }
    };

    useEffect(() => {
        if (mapRef.current !== null) {
            mapRef.current.events.remove('click');
            mapRef.current.geoObjects.events.remove('click');
            mapRef.current.events.add('click', () => {
                setFilterDrivesByIds([]);
            });
            mapRef.current.geoObjects.events.add('click', (e) => {
                if (e.get('target').geometry.getType() === 'Polygon') {
                    setFilterDrivesByIds([]);
                }
            });
        }
    }, [mapRef.current?.geoObjects]);

    const closeInfoWindow = () => {
        removePolyline();
        dispatch(loadGeoObjectsInInfo([]));
    };

    return (
        <div>
            {!showEmployeesByType ? <EmployeeList
                onFilterEmployees={(name) => {
                    dispatch(filterEmployeesByName({name: name, employees: baseEmployeesList}));
                }}
                itemHeadRight={(employee) => {
                    const filteredDrivesInRoutes = _.filter(drivesInRoutes, {courier: employee.id});
                    return filteredDrivesInRoutes.length ?
                        <span
                            onClick={(e) => {
                                e.stopPropagation();
                                setFilterDrivesByIds(_.map(filteredDrivesInRoutes, 'drive.id'));
                            }}
                            className={routeStyles.RouteLength}>{filteredDrivesInRoutes.length}</span>
                        : null;
                }}
                onEmployeeOpen={() => {}}
                itemClassName={() => {}}
                itemBody={(employee, isActive) => (
                    <EmployeeBody mapRef={mapRef} employee={employee} isActive={isActive}/>
                )}
                employees={employees}/> : null}
            <YMaps>
                <Map width="100%" height="100vh"
                     instanceRef={mapRef}
                     defaultState={{
                         center: [cityCenter.lat, cityCenter.lng], zoom: 10
                     }}>
                    {zonePolygons}
                    {employeesAddressesPlacemarks}
                    <Clusterer
                        onMouseenter={(e) => openShortBalloon(e)}
                        onMouseleave={(e) => closeBalloon(e)}
                        onClick={(e) => openInfoPanel(e)}
                        instanceRef={clustererRef}
                        options={{
                            groupByCoordinates: true,
                            disableClickZoom: true,
                            hasBalloon: false,
                            zIndex: 1
                        }}>
                        {drivePlacemarks}
                    </Clusterer>
                    {polyline}
                </Map>
            </YMaps>
            {authKey !== null ? <WsClient/> : null}
            <Info show={geoObjectsInInfoWindow.length > 0}
                  x={'20%'}
                  y={'10px'}
                  width={'400px'}
                  onClick={() => closeInfoWindow()}>
                {geoObjectsInInfoWindow.map((geoObject, key) => (
                    <OnsiteContent
                        onAddToRoute={(drive) => handleClickAddToRoute(drive)}
                        onDriveUpdate={(drive, props) => {
                            axios.put(API_BASE + '/drives/' + drive.id, props)
                                .then((response) => {
                                    dispatch(updateDrive({drive: drive, attributes: response.data}));
                                });
                        }}
                        drive={geoObject}
                        key={key}/>
                ))}
            </Info>
            <CentralPanel/>
            <FilterPanel
                onShowCleaners={(type) => {
                    setShowEmployeesByType(type);
                }}
                onApply={(filtersValue) => {
                    closeInfoWindow();
                    loadEmployees(filtersValue);
                    loadDrives(filtersValue);
                    dispatch(deactivateActiveItem());
                }}/>
        </div>
    );
}

export default OnsiteDrycleaningApp;
